import { AssetSchema } from "@/types";
import { z } from "zod";

// Lineage Table Schema
export const lineageTableSchema = z.object({
  table: z.string(),
  upstream_count: z.number(),
  downstream_count: z.number(),
  id: z.number().nullable(),
  materialization: z.string().nullable(),
  label: z.string(),
  schema_name: z.string(),
  resource_type: z.string(),
  tests: z.unknown().nullable(),
  source: z.string().nullable(),
  is_stale: z.boolean().nullable(),
  last_seen: z.string().nullable(),
});

export type LineageTable = z.infer<typeof lineageTableSchema>;

// Lineage Response Schema
export const lineageResponseSchema = z.object({
  tables: z.array(lineageTableSchema),
});

export type LineageResponse = z.infer<typeof lineageResponseSchema>;

// Column Schema for lineage
export const lineageColumnSchema = z.object({
  name: z.string(),
  rk: z.string(),
  datatype: z.string(),
  can_lineage_expand: z.boolean(),
  is_transformation: z.boolean(),
  description: z.string().nullable().optional(),
});

export type LineageColumn = z.infer<typeof lineageColumnSchema>;

// Get Columns Request
export interface GetColumnsRequest {
  table_id: string;
}

// Get Columns Response Schema
export const getColumnsResponseSchema = z.object({
  columns: z.array(lineageColumnSchema),
  purpose: z.string().nullable().optional(),
  table: z.string(),
  last_modified_by: z.number(),
});

export type GetColumnsResponse = z.infer<typeof getColumnsResponseSchema>;

// Terminal Node Request (for altimateToAmundsen)
export interface TerminalNodeRequest {
  table_id?: string;
  asset_rk?: string;
  asset_type?: string;
}

// Terminal Node Response - reuses lineageTableSchema
export type TerminalNodeResponse = LineageTable;
export const terminalNodeResponseSchema = lineageTableSchema;

// Connected Columns Edge Extra Info Schema
export const connectedColumnsEdgeExtraSchema = z.object({
  type: z.string(),
  lens_code: z.string(),
  lens_type: z.string(),
});

export type ConnectedColumnsEdgeExtra = z.infer<typeof connectedColumnsEdgeExtraSchema>;

// Connected Columns Edge Schema
export const connectedColumnsEdgeSchema = z.object({
  source: z.string(),
  target: z.string(),
  extra: connectedColumnsEdgeExtraSchema.nullable(),
});

export type ConnectedColumnsEdge = z.infer<typeof connectedColumnsEdgeSchema>;

// Connected Columns Request
export interface ConnectedColumnsRequest {
  column_fqns: string[];
  nodes: string[];
  upstreamExpansion: boolean;
  showIndirectEdges: boolean;
}

// Connected Columns Response Schema
export const connectedColumnsResponseSchema = z.object({
  highlight_edges: z.array(connectedColumnsEdgeSchema),
});

export type ConnectedColumnsResponse = z.infer<typeof connectedColumnsResponseSchema>;

// Upstream/Downstream Tables V2 Request
export interface UpstreamDownstreamTablesRequest {
  fqn: string;
}

export type Asset = z.infer<typeof AssetSchema>;

// Upstream/Downstream Tables V2 Response
export const upstreamDownstreamTablesResponseSchema = z.object({
  assets: z.array(AssetSchema),
});

export type UpstreamDownstreamTablesResponse = z.infer<
  typeof upstreamDownstreamTablesResponseSchema
>;

// Snowflake Stage Metadata Schema
export const snowflakeStageMetadataSchema = z.object({
  rk: z.string(),
  name: z.string(),
  stage_url: z.string().nullable().optional(),
  stage_region: z.string().nullable().optional(),
  stage_type: z.string().nullable().optional(),
  stage_owner: z.string().nullable().optional(),
});

export type SnowflakeStageMetadataResponse = z.infer<typeof snowflakeStageMetadataSchema>;

// DBT Info Schema (matches backend DbtInfo)
export const DbtInfoSchema = z.object({
  project_name: z.string().nullable().optional(),
  model_name: z.string().nullable().optional(),
  full_refresh: z.string().nullable().optional(),
  last_refreshed: z.string().nullable().optional(),
  last_run_cost: z.string().nullable().optional(),
  last_run_duration: z.string().nullable().optional(),
});

export type DbtInfo = z.infer<typeof DbtInfoSchema>;

// DBT Info Response Schema (matches backend DbtInfoResponse)
export const DbtInfoResponseSchema = z.object({
  dbt_info: DbtInfoSchema.nullable().optional(),
});

export type DbtInfoResponse = z.infer<typeof DbtInfoResponseSchema>;
